// Prints a patient's safety plan on the clinic's letterhead (Settings →
// Letterhead). Opens a print window; the clinician saves it as PDF or hands
// the paper copy to the patient.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const SAFETY_PLAN_STEPS = [
  { key: "warning_signs", title: "1. My warning signs", hint: "Thoughts, feelings or situations that tell me a crisis may be starting" },
  { key: "coping_strategies", title: "2. Things I can do on my own", hint: "To take my mind off things - without contacting anyone" },
  { key: "distractions", title: "3. People and places that help me feel better", hint: "Where I can go, who I can be around" },
  { key: "people_to_ask", title: "4. People I can ask for help", contacts: true },
  { key: "professionals", title: "5. Professionals I can contact", contacts: true },
  { key: "crisis_lines", title: "Crisis lines (free, 24x7)", lines: true },
  { key: "means_safety", title: "6. Making my surroundings safer", hint: "What I'll remove or lock away, and who will help" },
  { key: "reasons_for_living", title: "My reasons for living", hint: "What matters most to me" },
];

export function printSafetyPlan({ plan, patientName, header = {} }) {
  const real = (v) => (v && !String(v).startsWith("s3://") ? v : "");
  const logo = real(header.logo_display_url) || (String(header.logo_url || "").startsWith("http") || String(header.logo_url || "").startsWith("data:") ? header.logo_url : "");
  const section = (step) => {
    const value = plan[step.key] || [];
    if (!value.length) return "";
    let items;
    if (step.contacts) items = value.map((c) => `<li><b>${esc(c.name)}</b>${c.phone ? ` &middot; ${esc(c.phone)}` : ""}</li>`).join("");
    else if (step.lines) items = value.map((l) => `<li><b>${esc(l.number)}</b>${l.alt ? ` or ${esc(l.alt)}` : ""} &middot; ${esc(l.name)}</li>`).join("");
    else items = value.map((v) => `<li>${esc(v)}</li>`).join("");
    return `<section><h3>${esc(step.title)}</h3><ul>${items}</ul></section>`;
  };
  const date = new Date(plan.created_at || Date.now()).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Safety plan - ${esc(patientName)}</title>
<style>
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #1f2937; margin: 32px; }
  header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #be123c; padding-bottom: 12px; }
  header img { max-height: 56px; max-width: 160px; object-fit: contain; }
  header .org { font-size: 20px; font-weight: 700; } header .sub { font-size: 12px; color: #6b7280; }
  h1 { font-size: 22px; margin: 20px 0 2px; } .meta { color: #6b7280; font-size: 13px; margin-bottom: 16px; }
  section { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 14px; margin-bottom: 10px; break-inside: avoid; }
  h3 { margin: 0 0 6px; font-size: 15px; color: #9f1239; } ul { margin: 0; padding-left: 20px; } li { margin: 3px 0; font-size: 14px; }
  .emergency { background: #fff1f2; border-color: #fecdd3; font-size: 14px; }
  @media print { body { margin: 16mm; } }
</style></head><body>
<header>${logo ? `<img src="${esc(logo)}" alt="">` : ""}<div><div class="org">${esc(header.hospital_name || "")}</div>
<div class="sub">${[header.address, header.phone].filter(Boolean).map(esc).join(" &middot; ")}</div></div></header>
<h1>My safety plan</h1><div class="meta">${esc(patientName)} &middot; ${esc(date)} &middot; version ${esc(plan.version)}</div>
${SAFETY_PLAN_STEPS.map(section).join("")}
<section class="emergency"><b>If I'm in danger right now:</b> call <b>112</b>, or go to the nearest emergency department.</section>
<script>window.onload = () => { window.print(); };</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
